import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Table, Tag, Badge, Modal, Input, Spin } from "antd";
import { Phone, PhoneCall, PhoneOff, Calendar, Send, BarChart2, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AI_CALLER_DASHBOARD_URL, AI_CALLER_CALL_INITIATE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

// ── helpers ───────────────────────────────────────────────────────────────────

const OUTCOME_COLORS = {
    demo_booked: "green",
    qualified: "blue",
    disqualified: "red",
    no_answer: "default",
    callback_requested: "orange",
};

const STATUS_BADGE = {
    in_progress: "processing",
    completed: "success",
    failed: "error",
};

const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
};

// ── stat card ─────────────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon: Icon, borderColor, iconBg }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 border-l-4 ${borderColor}`}>
        <div className="flex items-start justify-between">
            <div className="flex-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {title}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {value?.toLocaleString() ?? "—"}
                </p>
            </div>
            <div className={`p-2 rounded-lg flex-shrink-0 ${iconBg}`}>
                <Icon className="h-5 w-5" />
            </div>
        </div>
    </div>
);

// ── main component ────────────────────────────────────────────────────────────

export default function AICallerDashboard() {
    const navigate = useNavigate();
    const { fetch, create } = useApiMethods();
    const { hasPermission } = useContext(AuthContext);

    const [stats, setStats] = useState(null);
    const [recentCalls, setRecentCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [callModalOpen, setCallModalOpen] = useState(false);
    const [customerId, setCustomerId] = useState("");
    const [initiating, setInitiating] = useState(false);

    const intervalRef = useRef(null);

    const loadDashboard = async () => {
        const res = await fetch(AI_CALLER_DASHBOARD_URL);
        if (res?.status === "success") {
            setStats(res.data?.stats || null);
            setRecentCalls(res.data?.recent_calls || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadDashboard();
        intervalRef.current = setInterval(loadDashboard, 30000);
        return () => clearInterval(intervalRef.current);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInitiateCall = async () => {
        if (!customerId.trim()) {
            ToastMsg("error", "Please enter a customer ID");
            return;
        }
        setInitiating(true);
        const res = await create(AI_CALLER_CALL_INITIATE_URL, { customer_id: customerId.trim() });
        setInitiating(false);
        if (res?.status === "success") {
            ToastMsg("success", "Call initiated");
            setCallModalOpen(false);
            setCustomerId("");
            loadDashboard();
        } else {
            ToastMsg("error", res?.message || "Failed to initiate call");
        }
    };

    // ── table columns ─────────────────────────────────────────────────────────

    const columns = [
        {
            title: "Customer Name",
            dataIndex: "customer_name",
            key: "customer_name",
            render: (text) => <span className="font-medium text-gray-800 dark:text-gray-100">{text || "—"}</span>,
        },
        {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Badge status={STATUS_BADGE[status] || "default"} text={status?.replace("_", " ") || "—"} />
            ),
        },
        {
            title: "Outcome",
            dataIndex: "outcome",
            key: "outcome",
            render: (outcome) => outcome ? (
                <Tag color={OUTCOME_COLORS[outcome] || "default"}>
                    {outcome.replace("_", " ").toUpperCase()}
                </Tag>
            ) : "—",
        },
        {
            title: "Duration",
            dataIndex: "duration_seconds",
            key: "duration_seconds",
            render: (val) => formatDuration(val),
        },
        {
            title: "Language",
            dataIndex: "language",
            key: "language",
            render: (val) => val || "—",
        },
        {
            title: "Date",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) => val ? dayjs(val).format("DD MMM YYYY, hh:mm A") : "—",
        },
    ];

    if (loading) {
        return (
            <ContainerCard>
                <div className="flex items-center justify-center py-24">
                    <Spin size="large" />
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center justify-between mb-4 p-2">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        <Phone className="h-5 w-5 text-teal-600" /> AI Caller Dashboard
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Real-time AI calling performance</p>
                </div>
                <Button
                    onClick={() => setCallModalOpen(true)}
                    className="flex items-center gap-2 text-sm"
                >
                    <PhoneCall className="h-4 w-4" /> Call Now
                </Button>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 px-2">
                <StatCard
                    title="Total Calls Today"
                    value={stats?.total_calls_today}
                    icon={Phone}
                    borderColor="border-l-blue-500"
                    iconBg="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                />
                <StatCard
                    title="Demos Booked"
                    value={stats?.demos_booked}
                    icon={Calendar}
                    borderColor="border-l-green-500"
                    iconBg="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                />
                <StatCard
                    title="Qualified Leads"
                    value={stats?.qualified_leads}
                    icon={BarChart2}
                    borderColor="border-l-purple-500"
                    iconBg="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
                />
                <StatCard
                    title="Active Calls"
                    value={stats?.active_calls}
                    icon={PhoneCall}
                    borderColor="border-l-orange-500"
                    iconBg="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400"
                />
            </div>

            {/* Week stats */}
            {stats?.week_stats && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6 mx-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">This Week</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Total Calls</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.week_stats.total_calls ?? "—"}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Avg Duration</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{formatDuration(stats.week_stats.avg_duration)}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Conversion Rate</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.week_stats.conversion_rate ?? "—"}%</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Demos This Week</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.week_stats.demos_booked ?? "—"}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent call logs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mx-2 mb-2">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Recent Calls</h3>
                    <button
                        onClick={() => navigate("/ai-caller/calls")}
                        className="text-xs text-teal-600 hover:text-teal-700 dark:text-teal-400 flex items-center gap-1"
                    >
                        View All <ChevronRight className="h-3 w-3" />
                    </button>
                </div>
                <Table
                    columns={columns}
                    dataSource={recentCalls}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: "No calls yet" }}
                    onRow={(record) => ({
                        onClick: () => navigate(`/ai-caller/calls/${record.id}`),
                        className: "cursor-pointer",
                    })}
                />
            </div>

            {/* Call Now Modal */}
            <Modal
                title="Initiate AI Call"
                open={callModalOpen}
                onCancel={() => { setCallModalOpen(false); setCustomerId(""); }}
                onOk={handleInitiateCall}
                confirmLoading={initiating}
                okText="Call"
            >
                <div className="py-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Customer ID
                    </label>
                    <Input
                        placeholder="Enter customer ID"
                        value={customerId}
                        onChange={(e) => setCustomerId(e.target.value)}
                        onPressEnter={handleInitiateCall}
                    />
                </div>
            </Modal>
        </ContainerCard>
    );
}
